import { z } from 'zod'
import { oc } from '@orpc/contract'
import {
  collectCredentialIssues,
  licenseRowSchema,
  npiNumberSchema,
  professionalRoleSchema,
  specialtySchema,
} from './member-credentials.contract'
import { updatePractitionerCredentialsOutputSchema } from './update-practitioner-credentials.contract'

/**
 * A state license as its holder may edit it on My Profile (see the glossary): the
 * Provider Operations row minus everything a coordinator owns. `.strict()` so a request
 * carrying the License Status, Restriction or the verification record is refused at the
 * boundary rather than silently dropped — a member can never verify themselves.
 */
export const selfLicenseRowSchema = licenseRowSchema
  .pick({
    state: true,
    licenseNumber: true,
    effectiveDate: true,
    expiryDate: true,
    document: true,
  })
  .strict()

export type SelfLicenseRow = z.infer<typeof selfLicenseRowSchema>

/**
 * The member's own Credentials: NPI, Professional Role, Specialty and the self rows. The
 * cross-row rules (one license per state, a Specialty the role carries, no license without
 * a role) are the ones Provider Operations enforces, so the two pages refuse the same input.
 * `.strict()` as the row is: a request carrying what a coordinator owns on the whole record
 * (the Payer Enrollments) is refused at the boundary rather than silently dropped.
 */
export const updateMyCredentialsInputSchema = z
  .object({
    npiNumber: npiNumberSchema.optional(),
    professionalRole: professionalRoleSchema.nullish(),
    specialty: specialtySchema.nullish(),
    licenses: z.array(selfLicenseRowSchema).default([]),
  })
  .strict()
  .superRefine((data, ctx) => {
    for (const issue of collectCredentialIssues(data)) {
      ctx.addIssue({ code: 'custom', ...issue })
    }
  })

export type UpdateMyCredentialsInput = z.infer<typeof updateMyCredentialsInputSchema>

/**
 * The caller's own Credentials. The member is the session's profile, as on the my-profile
 * read; the write runs on the admin client through the Provider Operations writer. Only a
 * member with an active Medical doctor role may call it; 403 otherwise.
 */
export const updateMyCredentials = oc
  // Under `me/profile/…`: `me/credentials` would be shadowed by the Provider Operations
  // route `POST /v2/practitioners/{practitionerId}/credentials` (`orpc/contracts.spec.ts`).
  .route({ method: 'POST', path: '/v2/practitioners/me/profile/credentials' })
  .input(updateMyCredentialsInputSchema)
  .output(updatePractitionerCredentialsOutputSchema)
